import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import React, { useEffect, useState } from "react";
import BarekamCheckbox from "../../../components/checkbox";
import BarekamText from "../../../components/text";
import { NotificationTemplate } from "../../../../redux/reducers/user-notifications.reducer";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import { getNotificationTemplates } from "../../../../redux/slices/user-notifications.slice";
import notificationTemplateService from "../../../../services/notification-template-service";
import { ToastContainer } from "react-toastify";
import { Skeleton } from "@mui/material";
import { GapContainer } from "../../../components/card/styled";
import { ActionBarWithSaveButton } from "../user-actionbar/action-bar-with-save-button";

export const UserNotifications = () => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [selectedStatusNotificationTemplates, setSelectedStatusNotificationTemplates] = useState<NotificationTemplate[]>([]);
    const [selectedTransactionNotificationTemplates, setSelectedTransactionNotificationTemplates] = useState<NotificationTemplate[]>([]);

    const { statusNotificationTemplates, transactionNotificationTemplates } = useAppSelector(state => state.userNotificationsReducer);

    const dispatch = useAppDispatch();

    useEffect(() => {
        Promise.allSettled([
            notificationTemplateService.getMyNotificationConfigs(),
            dispatch(getNotificationTemplates({ isConfigurable: true }))
        ]).then(([notificationConfigsResponse,]) => {
            setIsReady(true);
            const data = notificationConfigsResponse.status === 'fulfilled' ? notificationConfigsResponse['value'] : [];

            if (data && data.length) {
                const statusTemplates = data.filter((template) => template.templateType === 1);
                const transactionTemplates = data.filter((template) => template.templateType === 2);
                setSelectedStatusNotificationTemplates(statusTemplates);
                setSelectedTransactionNotificationTemplates(transactionTemplates);
            }
        })
    }, []);

    function onNotificationCheckboxChange(template: NotificationTemplate, checked: boolean) {
        const templates = template.templateType === 1 ? [...selectedStatusNotificationTemplates] : [...selectedTransactionNotificationTemplates];
        if (checked) {
            templates.push(template)
            template.templateType === 1 ? setSelectedStatusNotificationTemplates(templates) : setSelectedTransactionNotificationTemplates(templates);
        } else {
            const index = templates.findIndex((item) => item.notificationTemplateId === template.notificationTemplateId);
            templates.splice(index, 1);
            template.templateType === 1 ? setSelectedStatusNotificationTemplates(templates) : setSelectedTransactionNotificationTemplates(templates);
        }
    }

    function handleSelectAll(selectAll: boolean) {
        if (selectAll) {
            setSelectedStatusNotificationTemplates(statusNotificationTemplates.templates);
            setSelectedTransactionNotificationTemplates(transactionNotificationTemplates.templates);
        } else {
            setSelectedStatusNotificationTemplates([]);
            setSelectedTransactionNotificationTemplates([]);
        }
    }

    const SelectAllNotificationRow = () => {
        const isChecked = selectedStatusNotificationTemplates.length === statusNotificationTemplates.templates.length && selectedTransactionNotificationTemplates.length === transactionNotificationTemplates.templates.length;
        const isIndeterminate = !isChecked && !!(selectedTransactionNotificationTemplates.length || selectedStatusNotificationTemplates.length);

        return (
            <GapContainer>
                <BarekamCheckbox onClick={() => handleSelectAll(!isChecked || isIndeterminate)}
                    indeterminate={isIndeterminate}
                    checked={isChecked} />

                <BarekamText variant={'m'} opaque>
                    <Message messageKey={'all_templates'} sectionId={MessageSections.USER} />
                </BarekamText>
            </GapContainer>
        )
    }

    const EditNotificationRow = (template: NotificationTemplate) => {
        const isChecked = template.templateType === 1 ? !!selectedStatusNotificationTemplates.find((selectedTemplate) => selectedTemplate.notificationTemplateId === template.notificationTemplateId) :
            !!selectedTransactionNotificationTemplates.find((selectedTemplate) => selectedTemplate.notificationTemplateId === template.notificationTemplateId);
        return (
            <GapContainer>
                <BarekamCheckbox onClick={() => onNotificationCheckboxChange(template, !isChecked)}
                    checked={isChecked} />

                <BarekamText variant={'m'} opaque>
                    <Message messageKey={template.subject} sectionId={MessageSections.USER} />
                </BarekamText>
            </GapContainer>
        )
    }

    const HeaderText = () => (
        <BarekamText variant={"m"} opaque>
            <Message messageKey={'notifications_hint'} sectionId={MessageSections.USER} />
        </BarekamText>
    )

    const PrimaryActionText = () => (
        <BarekamText variant={"m"} bold>
            <Message messageKey={'save'} sectionId={MessageSections.USER} />
        </BarekamText>
    )

    async function handleSave() {
        const updateData = [...selectedTransactionNotificationTemplates];
        updateData.push(...selectedStatusNotificationTemplates);
        await notificationTemplateService.updateMyNotificationConfigs(updateData);
    }

    return (
        <>
            <ActionBarWithSaveButton headerText={<HeaderText />}
                primaryActionText={<PrimaryActionText />}
                onPrimaryActionClick={handleSave} />

            {isReady
                ? <>
                    <SelectAllNotificationRow />
                    {statusNotificationTemplates.templates.map(notificationData => {
                        return <EditNotificationRow notificationTemplateId={notificationData.notificationTemplateId}
                            subject={notificationData.subject}
                            body={notificationData.body}
                            section={notificationData.section}
                            templateType={1}
                            key={`status-${notificationData.notificationTemplateId}`} />
                    })}
                    {transactionNotificationTemplates.templates.map(notificationData => {
                        return <EditNotificationRow notificationTemplateId={notificationData.notificationTemplateId}
                            subject={notificationData.subject}
                            body={notificationData.body}
                            section={notificationData.section}
                            templateType={2}
                            key={`transaction-${notificationData.notificationTemplateId}`} />
                    })}

                </>
                : <>
                    <Skeleton width={'40%'} height={'0.85rem'} />
                </>
            }

            <ToastContainer />
        </>
    )
}
